/**
 * Auto-Localization Fix for Shopify Markets
 *
 * PROBLEM: Site shows New Zealand (NZ) when it should detect Spain (ES)
 * SOLUTION: Auto-detect user's country and update Shopify localization
 *
 * This script:
 * 1. Detects user's actual country via browser timezone/language
 * 2. Validates against Shopify's available markets
 * 3. Auto-updates the localization form if mismatch detected
 * 4. Tracks conversion impact for A/B testing
 *
 * NOTE: This script respects A/B test assignment. Only runs for "variation" group.
 */

(function() {
  // Check A/B test assignment - only run for variation group
  var testId = 'test_localization_auto';
  var storageKey = 'split_test_' + testId;
  var assignment = localStorage.getItem(storageKey);

  // If no assignment yet, assign randomly (50/50 split)
  if (!assignment) {
    assignment = Math.random() < 0.5 ? 'control' : 'variation';
    localStorage.setItem(storageKey, assignment);
    console.log('[Auto-Localization] Assigned to:', assignment);
  }

  // Only run for variation group
  if (assignment !== 'variation') {
    console.log('[Auto-Localization] Control group - not running');
    return;
  }

  console.log('[Auto-Localization] Variation group - starting country detection...');

  // Get current Shopify localization state from the page
  function getCurrentShopifyCountry() {
    // Check the localization form for current country
    const countryInput = document.querySelector('input[name="country_code"]');
    if (countryInput) {
      return countryInput.value;
    }

    // Check the displayed country in the selector
    const countryDisplay = document.querySelector('.localization-selector');
    if (countryDisplay) {
      const text = countryDisplay.textContent;
      // Extract ISO code from text like "New Zealand (NZD $)" or "España (EUR €)"
      const match = text.match(/\(([A-Z]{3})/);
      if (match) {
        // Map currency to country (approximate)
        const currencyToCountry = {
          'NZD': 'NZ',
          'EUR': 'ES', // Assume EUR = Spain for this site
          'USD': 'US',
          'GBP': 'GB',
          'AUD': 'AU'
        };
        return currencyToCountry[match[1]];
      }
    }

    return null;
  }

  // Detect user's actual country
  function detectUserCountry() {
    // Method 1: Browser language
    const browserLang = navigator.language || navigator.userLanguage;
    console.log('[Auto-Localization] Browser language:', browserLang);

    if (browserLang.startsWith('es')) {
      return 'ES'; // Spanish
    }

    // Method 2: Timezone detection (approximate)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('[Auto-Localization] Timezone:', timezone);

    // Map timezones to countries
    const timezoneMap = {
      'Europe/Madrid': 'ES',
      'Europe/Barcelona': 'ES',
      'Europe/London': 'GB',
      'America/New_York': 'US',
      'Pacific/Auckland': 'NZ',
      'Australia/Sydney': 'AU'
    };

    if (timezoneMap[timezone]) {
      return timezoneMap[timezone];
    }

    // If timezone starts with "Europe/" and language is Spanish, assume Spain
    if (timezone.startsWith('Europe/') && browserLang.startsWith('es')) {
      return 'ES';
    }

    return null;
  }

  // Get available countries from Shopify localization form
  function getAvailableCountries() {
    const countries = [];
    const countryLinks = document.querySelectorAll('.disclosure__link[data-value]');

    countryLinks.forEach(link => {
      const countryCode = link.getAttribute('data-value');
      if (countryCode) {
        countries.push(countryCode);
      }
    });

    console.log('[Auto-Localization] Available countries:', countries);
    return countries;
  }

  // Update Shopify localization form
  function updateLocalization(newCountryCode) {
    console.log('[Auto-Localization] Updating to country:', newCountryCode);

    // Find the country link in the dropdown
    const countryLink = document.querySelector(`.disclosure__link[data-value="${newCountryCode}"]`);

    if (!countryLink) {
      console.warn('[Auto-Localization] Country not available in Shopify Markets:', newCountryCode);
      return false;
    }

    // Update the hidden input
    const countryInput = document.querySelector('input[name="country_code"]');
    if (countryInput) {
      countryInput.value = newCountryCode;
    }

    // Submit the localization form
    const localizationForm = document.querySelector('form[action*="/localization"]');
    if (localizationForm) {
      console.log('[Auto-Localization] Submitting localization form...');

      // Track the change
      if (window.SplitTestEngine) {
        window.SplitTestEngine.trackCustomEvent('auto_localization_triggered', {
          from_country: getCurrentShopifyCountry(),
          to_country: newCountryCode,
          detection_method: 'browser_timezone'
        });
      }

      // Submit the form to update localization
      localizationForm.submit();
      return true;
    }

    console.error('[Auto-Localization] Could not find localization form');
    return false;
  }

  // Main logic
  function autoFixLocalization() {
    // Check if user has already been through this process
    const hasAutoLocalized = sessionStorage.getItem('auto_localized');
    if (hasAutoLocalized === 'true') {
      console.log('[Auto-Localization] Already auto-localized this session');
      return;
    }

    // Get current and detected countries
    const currentCountry = getCurrentShopifyCountry();
    const detectedCountry = detectUserCountry();

    console.log('[Auto-Localization] Current country:', currentCountry);
    console.log('[Auto-Localization] Detected country:', detectedCountry);

    // If detection failed or matches current, do nothing
    if (!detectedCountry || detectedCountry === currentCountry) {
      console.log('[Auto-Localization] No change needed');
      sessionStorage.setItem('auto_localized', 'true');
      return;
    }

    // Check if detected country is available
    const availableCountries = getAvailableCountries();
    if (!availableCountries.includes(detectedCountry)) {
      console.warn('[Auto-Localization] Detected country not in available markets:', detectedCountry);
      sessionStorage.setItem('auto_localized', 'true');
      return;
    }

    // MISMATCH DETECTED - Update localization
    console.log('[Auto-Localization] MISMATCH! Will update from', currentCountry, 'to', detectedCountry);

    // Wait a bit to ensure page is fully loaded
    setTimeout(() => {
      const success = updateLocalization(detectedCountry);
      if (success) {
        sessionStorage.setItem('auto_localized', 'true');
      }
    }, 1000);
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoFixLocalization);
  } else {
    autoFixLocalization();
  }
})();
